import {
  HStack,
  Text,
  VStack,
  Card,
  CardHeader,
  Select,
  CardBody,
  CardFooter,
  Button,
  Box,
  Heading,
  Center,
  Spinner,
  IconButton,
  ButtonGroup,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDescription,
  AlertTitle,
  useDisclosure,
  useToast,
  Input,
  useMediaQuery,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { useState, useEffect } from 'react';
import useUser from '../hooks/useUser';
import axios from 'axios';
import EstimateStatus from './estimate/EstimateStatus';
import { useNavigate } from 'react-router-dom';
import React from 'react';

const History = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [roofToDelete, setRoofToDelete] = useState(null);
  const [roofList, setRoofList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSalesperson, setSelectedSalesperson] =
    useState('All salespeople');
  const [filteredRoofList, setFilteredRoofList] = useState([]);
  const [salespersonList, setSalespersonList] = useState(['All salespeople']);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const { user } = useUser();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const [isLargerThan800] = useMediaQuery('(min-width: 800px)');

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        const token = await user.getIdToken();
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/roofs/history`,
          {
            headers: {
              authorization: `${token}`,
            },
            params: {
              username: user.email,
            },
          }
        );
        setRoofList(response.data);
        setFilteredRoofList(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setIsLoading(false);
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    const newSalespersonsSet = new Set(salespersonList);
    roofList.forEach(roof => {
      newSalespersonsSet.add(roof.createdBy);
    });
    const newSalespersonsArray = Array.from(newSalespersonsSet).sort();
    setSalespersonList(newSalespersonsArray);
  }, [roofList]);

  // useEffect(() => {
  //   console.log(roofList);
  //   const tempList = [...salespersonList];
  //   roofList.forEach(roof => {
  //     if (!tempList.includes(roof.createdBy)) {
  //       tempList.push(roof.createdBy);
  //     }
  //   });
  //   setSalespersonList(tempList);
  // }, [roofList]);

  const handleOpen = async id => {
    try {
      const token = await user.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/current_estimate`,
        {
          username: user.email,
          estimateId: id,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      navigate('/estimator');
      window.location.reload();
    } catch (error) {
      console.error('Error setting estimate:', error);
    }
  };

  const handleDelete = id => {
    setRoofToDelete(id);
    onOpen();
  };

  const filterRoofs = (salesperson, search) => {
    const filteredRoofs = [];

    for (const roof of roofList) {
      const matchesSalesperson =
        salesperson === 'All salespeople' || roof.createdBy === salesperson;

      const matchesSearch = roof?.name
        ?.toLowerCase()
        .includes(search.toLowerCase());

      if (matchesSalesperson && matchesSearch) {
        filteredRoofs.push(roof);
      }
    }

    setFilteredRoofList(filteredRoofs);
  };

  const handleSearchRoofs = event => {
    const search = event.target.value;

    setSearchTerm(search);
    filterRoofs(selectedSalesperson, search);
  };

  const handleFilterRoofsBySalesperson = event => {
    const salesperson = event.target.value;

    setSelectedSalesperson(salesperson);
    filterRoofs(salesperson, searchTerm);
  };

  const onDeleteConfirm = async () => {
    try {
      setIsDeleteLoading(true);
      const token = await user.getIdToken();
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/roofs/delete/${user.email}/${roofToDelete}`,
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      setIsDeleteLoading(false);
      const removedRoof = roofList.find(roof => roof.id === roofToDelete);
      const filteredList = roofList.filter(roof => roof.id !== roofToDelete);
      if (removedRoof.isCurrent) {
        filteredList[0].isCurrent = true;
      }

      setRoofList(filteredList);
      setFilteredRoofList(filteredList);
      onClose();
    } catch (error) {
      console.error(error);
      setIsDeleteLoading(false);
    }
  };

  return (
    <Box h="90vh" overflowY="auto">
      <HStack justify="center" pt="50px">
        {isLoading ? (
          <Center>
            <Spinner
              thickness="7px"
              speed="1.25s"
              size="xl"
              emptyColor="gray.200"
              color="black.500"
              marginTop="100px"
            />
          </Center>
        ) : (
          <VStack spacing="10px" pb={10}>
            {user?.profile === 'admin' && (
              <Select
                size="sm"
                value={selectedSalesperson}
                onChange={handleFilterRoofsBySalesperson}
              >
                {salespersonList.map(salesperson => (
                  <option key={salesperson} value={salesperson}>
                    {salesperson}
                  </option>
                ))}
              </Select>
            )}
            <Input
              size="sm"
              mt={2}
              w={{ base: '92vw', sm: '500px' }}
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchRoofs}
            />
            {filteredRoofList.map(item => {
              const statusLines = (
                <>
                  {user?.profile === 'admin' && (
                    <Text fontSize="sm">{item.createdBy}</Text>
                  )}
                  <EstimateStatus estimate={item} />
                </>
              );

              return (
                <Card
                  w={{ base: '92vw', sm: '500px' }}
                  variant={item.isCurrent ? 'filled' : 'outline'}
                  key={item.id}
                >
                  <CardBody>
                    {isLargerThan800 ? (
                      <HStack width="100%" spacing={4}>
                        <Text fontSize="sm" w="100px" flexShrink={0}>
                          {item.date ?? '---'}
                        </Text>
                        <VStack align="flex-start" flex={1} minW={0}>
                          <Text as="b">
                            {(item.number ? '#' + item.number + ' - ' : '') +
                              ' ' +
                              item.name}
                          </Text>
                          {statusLines}
                        </VStack>
                        <ButtonGroup gap="0" flexShrink={0}>
                          <Button
                            size="sm"
                            colorScheme="blackAlpha"
                            onClick={() => {
                              handleOpen(item.id);
                            }}
                          >
                            Open
                          </Button>
                          <IconButton
                            colorScheme="red"
                            size="sm"
                            aria-label="Delete roof"
                            onClick={() => {
                              handleDelete(item.id);
                            }}
                            icon={<DeleteIcon />}
                          />
                        </ButtonGroup>
                      </HStack>
                    ) : (
                      <VStack align="stretch" spacing={2} width="100%">
                        <HStack justify="space-between" width="100%">
                          <Text as="b" fontSize="sm" noOfLines={1}>
                            {(item.number ? '#' + item.number + ' - ' : '') +
                              ' ' +
                              item.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500" flexShrink={0}>
                            {item.date ?? '---'}
                          </Text>
                        </HStack>
                        <VStack align="flex-start" spacing={0.5}>
                          {statusLines}
                        </VStack>
                        <Button
                          size="sm"
                          colorScheme="blackAlpha"
                          w="100%"
                          onClick={() => {
                            handleOpen(item.id);
                          }}
                        >
                          Open
                        </Button>
                      </VStack>
                    )}
                  </CardBody>
                </Card>
              );
            })}
          </VStack>
        )}
      </HStack>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Roof
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="flex-start" spacing="20px">
                {roofList.length > 1 ? (
                  <Text>Are you sure? You can't undo this action.</Text>
                ) : (
                  <Text>You must have at least one estimate.</Text>
                )}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              {roofList.length > 1 && (
                <Button colorScheme="red" onClick={onDeleteConfirm} ml={3}>
                  {isDeleteLoading ? (
                    <Spinner
                      thickness="2px"
                      speed="1.25s"
                      size="sm"
                      color="black.500"
                    />
                  ) : (
                    <Text>Delete</Text>
                  )}
                </Button>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default History;
